import { Fragment, useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import {
  Plus, Ban, CheckCircle2, Trash2, Edit3, UserCog, Clock,
  ChevronDown, ChevronRight, KeyRound, Pencil, Copy, Check, X, MoreVertical, Receipt, AlertTriangle, QrCode,
  FileCheck2, ExternalLink, FileClock, Sparkles,
} from "lucide-react";
import { generateTempPassword } from "@/lib/tempPassword";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import TenantBillingModal from "./TenantBillingModal";
import TenantPackageModal from "./TenantPackageModal";
import { Portal } from "@/components/Portal";
import QRStickerModal from "@/components/QRStickerModal";

const ROLE_LABEL = { owner: "Admin", manager: "Manager", supervisor: "Supervisor", staff: "Staff" };

export default function Tenants() {
  const { user: me } = useAuth();
  const { confirm, notify, prompt } = useDialog();
  const [tenants, setTenants] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [addOns, setAddOns] = useState([]);
  const [usersByTenant, setUsersByTenant] = useState({});
  const [show, setShow] = useState(null); // "create" | "edit"
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [editingUser, setEditingUser] = useState(null);
  const [billingTenant, setBillingTenant] = useState(null);
  const [billingAutoGen, setBillingAutoGen] = useState(false);
  const [packageTenant, setPackageTenant] = useState(null);
  const [showTenantAddons, setShowTenantAddons] = useState(false);
  const [qrTenant, setQrTenant] = useState(null);
  const [outstanding, setOutstanding] = useState({ pending: [], lapsed: [] });
  const [tempPwd, setTempPwd] = useState(null);
  const [copied, setCopied] = useState(false);

  const myTier = (me?.staff_tier || "admin").toLowerCase();
  const writeable = myTier !== "observer";

  const load = async () => {
    const [a, b, u, o, ao] = await Promise.all([
      api.get("/admin/tenants"),
      api.get("/admin/tiers"),
      api.get("/users"),
      api.get("/admin/payments/outstanding").catch(() => ({ data: { pending: [], lapsed: [] } })),
      api.get("/admin/add-ons").catch(() => ({ data: { add_ons: [] } })),
    ]);
    setTenants(a.data.tenants || []);
    setTiers(b.data.tiers || []);
    setAddOns(ao.data.add_ons || []);
    setOutstanding({ pending: o.data.pending || [], lapsed: o.data.lapsed || [] });
    const grouped = {};
    for (const usr of u.data.users || []) {
      if (!usr.tenant_id) continue;
      (grouped[usr.tenant_id] = grouped[usr.tenant_id] || []).push(usr);
    }
    Object.values(grouped).forEach((arr) =>
      arr.sort((x, y) => (ROLE_RANK[x.role] - ROLE_RANK[y.role]) || x.name.localeCompare(y.name))
    );
    setUsersByTenant(grouped);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({
      company_name: "", tier_id: tiers.find(t => t.name === "Trial")?.id || tiers[0]?.id,
      billing_model: "invoiced", billing_interval: "monthly",
      trial_days: 14, grace_days: 7,
      service_start: new Date().toISOString().slice(0, 10), duration_months: 12,
      enabled_addons: [],
      owner_name: "", owner_email: "", owner_password: "",
    });
    setShowTenantAddons(false);
    setShow("create"); setErr("");
  };
  const openEdit = (t) => {
    setForm({ ...t, enabled_addons: t.enabled_addons || [] });
    setShowTenantAddons((t.enabled_addons || []).length > 0);
    setShow("edit"); setErr("");
  };

  const toggleAddon = (key) => {
    setForm((f) => {
      const cur = new Set(f.enabled_addons || []);
      cur.has(key) ? cur.delete(key) : cur.add(key);
      return { ...f, enabled_addons: Array.from(cur) };
    });
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      if (show === "create") {
        await api.post("/admin/tenants", form);
      } else {
        const payload = { company_name: form.company_name, tier_id: form.tier_id, is_active: form.is_active };
        await api.put(`/admin/tenants/${form.id}`, payload);
        await api.put(`/admin/tenants/${form.id}/add-ons`, { enabled_addons: form.enabled_addons || [] });
      }
      setShow(null); load();
    } catch (e) { setErr(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (t) => {
    await api.put(`/admin/tenants/${t.id}`, { is_active: !t.is_active });
    load();
  };
  const impersonate = async (t) => {
    if (!await confirm({
      title: `Enter Support Mode as ${t.company_name}?`,
      message: `You will sign in as the tenant owner for the next 15 minutes. Every action you take is logged to the platform audit log. Your super-admin session is preserved and you can exit support mode at any time.`,
      confirmLabel: "Enter Support Mode",
    })) return;
    try {
      const { data } = await api.post(`/admin/tenants/${t.id}/impersonate`);
      // Stash the admin token so exit-support-mode can restore it.
      const adminTok = localStorage.getItem("vb_access_token");
      if (adminTok) localStorage.setItem("vb_admin_token", adminTok);
      // Swap in the short-lived impersonation token + clear any stale site
      // context left over from a previous session.
      localStorage.setItem("vb_access_token", data.access_token);
      localStorage.removeItem("vb_active_site");
      sessionStorage.setItem(
        "vb_support_meta",
        JSON.stringify({ tenant: t.company_name, expiresAt: data.expires_in_seconds }),
      );
      // Hard-navigate so React bootstraps with the new identity.
      window.location.assign("/app");
    } catch (e) { await notify({ title: "Support Mode failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };
  const extendTrial = async (t) => {
    const days = await prompt({
      title: `Extend trial for ${t.company_name}`,
      message: "By how many days should the trial be extended?",
      defaultValue: "30",
      inputType: "number",
      confirmLabel: "Extend trial",
    });
    if (days === null || !days || Number(days) <= 0) return;
    try { await api.post(`/admin/tenants/${t.id}/extend-trial`, { days: Number(days) }); load(); }
    catch (e) { await notify({ title: "Trial extension failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };
  const del = async (t) => {
    if (!await confirm({
      title: `Permanently delete ${t.company_name}?`,
      message: `This will erase the tenant and ALL their data: users, sites, equipment, CCP readings, deviations, knowledge base. This cannot be undone.`,
      danger: true,
      confirmLabel: "Delete tenant + all data",
    })) return;
    try { await api.delete(`/admin/tenants/${t.id}`); load(); await notify({ title: "Tenant deleted", variant: "ok" }); }
    catch (e) { await notify({ title: "Delete failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const resetUserPwd = async (u) => {
    if (!await confirm({
      title: `Reset ${u.name}'s password?`,
      message: "We'll generate a temporary password to share with them. They'll be required to set their own password on next login.",
      confirmLabel: "Generate temp password",
    })) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPwd({ user: u, password: data.temporary_password });
      setCopied(false);
    } catch (e) { await notify({ title: "Reset failed", message: formatErr(e.response?.data?.detail), variant: "err" }); }
  };
  const copyTemp = async () => {
    try { await navigator.clipboard.writeText(tempPwd.password); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { console.debug("Clipboard write failed:", e); }
  };

  const expandAll = () => setExpanded(new Set(tenants.map((t) => t.id)));
  const collapseAll = () => setExpanded(new Set());

  // Tenants that need a renewal: in grace, expired (read-only), or ending ≤30 days.
  const renewalsDue = tenants.filter((t) => {
    const s = t.billing_state;
    if (!s || s.status === "suspended" || s.status === "demo") return false;
    return s.status === "grace" || s.status === "expired" ||
      (s.days_until_access_end != null && s.days_until_access_end <= 30);
  });

  return (
    <div className="vb-fade-in" data-testid="tenants-page">
      <div className="flex justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>Customer tenants</h1>
          <p className="opacity-70 max-w-2xl">
            Every customer workspace on the Vanguard Beacon platform. Expand a row to see the customer's team — VB staff can reset
            individual passwords here. New users on a customer's team are added by the customer themselves, not us.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button className="vb-btn ghost" onClick={expandAll} data-testid="expand-all-tenants">Expand all</button>
          <button className="vb-btn ghost" onClick={collapseAll} data-testid="collapse-all-tenants">Collapse all</button>
          {writeable && (
            <button className="vb-btn" onClick={openCreate} data-testid="add-tenant"><Plus size={16} /> New tenant</button>
          )}
        </div>
      </div>

      {(outstanding.pending.length > 0 || outstanding.lapsed.length > 0) && (
        <div className="vb-card p-4 mb-5" style={{ borderLeft: "3px solid var(--vb-warning)" }} data-testid="receivables-banner">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} style={{ color: "var(--vb-warning)" }} />
            <strong className="vb-heading" style={{ color: "var(--vb-text-2)" }}>Outstanding & renewals</strong>
          </div>
          <div className="space-y-1 text-sm">
            {outstanding.pending.map((p) => (
              <div key={p.id} className="flex items-center gap-2" data-testid={`receivable-pending-${p.id}`}>
                <span className="vb-pill warn text-[10px]">Pending</span>
                <span><strong>{p.company_name || "—"}</strong> · {p.invoice_number || "no invoice #"} · due by {p.period_end ? new Date(p.period_end).toLocaleDateString() : "—"}</span>
              </div>
            ))}
            {outstanding.lapsed.map((l) => (
              <div key={l.tenant_id} className="flex items-center gap-2" data-testid={`receivable-lapsed-${l.tenant_id}`}>
                <span className="vb-pill err text-[10px]">Lapsed</span>
                <span><strong>{l.company_name || "—"}</strong> · subscription ended {l.period_end ? new Date(l.period_end).toLocaleDateString() : "—"} — renewal due</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {renewalsDue.length > 0 && (
        <div className="vb-card p-4 mb-5" style={{ borderLeft: "3px solid var(--vb-accent)" }} data-testid="renewals-panel">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={16} style={{ color: "var(--vb-accent)" }} />
            <strong className="vb-heading" style={{ color: "var(--vb-text-2)" }}>Renewals due ({renewalsDue.length})</strong>
          </div>
          <div className="space-y-2">
            {renewalsDue.map((t) => {
              const s = t.billing_state;
              const when = s.status === "expired" ? `read-only since ${fmtD(s.hard_block_at)}`
                : s.status === "grace" ? `in grace — ${s.days_until_hard_block ?? 0}d before lock`
                : `renews in ${s.days_until_access_end}d (${fmtD(s.access_until)})`;
              return (
                <div key={t.id} className="flex items-center justify-between gap-2 flex-wrap" data-testid={`renewal-${t.id}`}>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <LifecycleBadge state={s} />
                    <strong style={{ color: "var(--vb-text-2)" }}>{t.company_name}</strong>
                    <span className="opacity-60">· {when}</span>
                    {t.package_summary?.has && (
                      <span className="opacity-60">· {fmtSGD(t.package_summary.locked_price)}/{t.package_summary.billing_interval}</span>
                    )}
                  </div>
                  {writeable && (
                    <button className="vb-btn sm" onClick={() => { setBillingTenant(t); setBillingAutoGen(true); }}
                      data-testid={`renew-${t.id}`}>
                      <Plus size={13} /> Generate renewal invoice
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="vb-card overflow-x-auto"><table className="vb-table">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            <th>Company</th><th>Tier</th>
            <th>End date</th><th>Created</th><th>Status</th><th style={{ minWidth: 150 }}></th>
          </tr>
        </thead>
        <tbody>
          {tenants.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No tenants yet.</td></tr>}
          {tenants.map((t) => {
            const isOpen = expanded.has(t.id);
            const tenantUsers = usersByTenant[t.id] || [];
            return (
              <Fragment key={t.id}>
                <tr data-testid={`tenant-row-${t.id}`}>
                  <td onClick={() => toggleExpand(t.id)} style={{ cursor: "pointer" }} data-testid={`tenant-toggle-${t.id}`}>
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </td>
                  <td onClick={() => toggleExpand(t.id)} style={{ cursor: "pointer" }}>
                    <strong>{t.company_name}</strong>
                  </td>
                  <td>
                    <span className="vb-pill">
                      {t.tier_name
                        ? `${t.tier_name}${(t.package_summary?.addons_count > 0 || (t.enabled_addons || []).length > 0) ? "+" : ""}`
                        : "—"}
                    </span>
                  </td>
                  <td className="opacity-70">{fmtD(t.billing_state?.access_until)}</td>
                  <td className="opacity-70">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td><LifecycleBadge state={t.billing_state} /></td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <div className="flex gap-1 justify-end items-center">
                    {writeable && (
                      <button className="vb-btn ghost sm" onClick={() => impersonate(t)}
                        title="Sign in as this tenant (Support Mode)"
                        data-testid={`impersonate-${t.id}`}>
                        <UserCog size={13} /> Support
                      </button>
                    )}
                    {writeable && (
                      <button className="vb-btn ghost sm" onClick={() => toggleActive(t)}
                        title={t.is_active ? "Suspend" : "Reactivate"}
                        data-testid={`suspend-${t.id}`}
                        style={{ color: t.is_active ? "var(--vb-warning)" : "var(--vb-accent)" }}>
                        {t.is_active ? <><Ban size={13} /> Suspend</> : <><CheckCircle2 size={13} /> Reactivate</>}
                      </button>
                    )}
                    {writeable && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="vb-btn ghost sm" title="More actions"
                            data-testid={`more-actions-${t.id}`}>
                            <MoreVertical size={14} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-44"
                          style={{
                            background: "var(--vb-bg)",
                            border: "1px solid var(--vb-border)",
                            color: "var(--vb-text-1)",
                          }}>
                          <DropdownMenuItem onClick={() => extendTrial(t)}
                            data-testid={`extend-trial-${t.id}`}>
                            <Clock size={13} className="mr-2" /> Extend trial
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setPackageTenant(t)}
                            data-testid={`package-${t.id}`}>
                            <Receipt size={13} className="mr-2" /> Package & pricing
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setBillingTenant(t)}
                            data-testid={`billing-${t.id}`}>
                            <Receipt size={13} className="mr-2" /> Billing / invoices
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(t)}
                            data-testid={`edit-tenant-${t.id}`}>
                            <Edit3 size={13} className="mr-2" /> Edit company
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setQrTenant(t)}
                            data-testid={`qr-stickers-${t.id}`}>
                            <QrCode size={13} className="mr-2" /> QR stickers
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => del(t)}
                            style={{ color: "var(--vb-error)" }}
                            data-testid={`delete-tenant-${t.id}`}>
                            <Trash2 size={13} className="mr-2" /> Delete tenant…
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    </div>
                  </td>
                </tr>
                {isOpen && (
                  <tr key={`${t.id}-detail`} data-testid={`tenant-detail-${t.id}`}>
                    <td></td>
                    <td colSpan={5} style={{ background: "rgba(120, 61, 25, 0.04)", padding: 0 }}>
                      <TermsAcceptanceBanner tenant={t} />
                      {tenantUsers.length === 0 ? (
                        <div className="p-4 text-sm opacity-60">No users in this company yet — the customer's owner will invite their team.</div>
                      ) : (
                        <table className="vb-table" style={{ background: "transparent" }}>
                          <thead>
                            <tr style={{ fontSize: 11, textTransform: "uppercase", opacity: 0.7 }}>
                              <th>Name</th><th>Email / Username</th><th>Role</th><th>Joined</th><th style={{ width: 110 }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {tenantUsers.map((u) => (
                              <tr key={u.id} data-testid={`tenant-user-row-${u.id}`}>
                                <td>
                                  <strong>{u.name}</strong>
                                  {u.must_reset_password && <span className="vb-pill warn ml-2 text-[10px]">pwd reset pending</span>}
                                </td>
                                <td>{u.email}</td>
                                <td><span className="vb-pill">{ROLE_LABEL[u.role] || u.role}</span></td>
                                <td className="opacity-70">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                                <td>
                                  <div className="flex gap-1 justify-end items-center" style={{ whiteSpace: "nowrap" }}>
                                    {writeable && (
                                      <button className="vb-btn ghost sm" title="Edit name / email" onClick={() => setEditingUser(u)} data-testid={`edit-user-${u.id}`}>
                                        <Pencil size={12} />
                                      </button>
                                    )}
                                    {writeable && (
                                      <button className="vb-btn ghost sm" title="Reset password" onClick={() => resetUserPwd(u)} data-testid={`reset-pwd-${u.id}`}>
                                        <KeyRound size={13} />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table></div>

      {show && (
        <Portal><div className="vb-modal-bg"><div className="vb-modal" style={{ maxHeight: "90vh", overflowY: "auto" }}>
          <h2>{show === "create" ? "Onboard new tenant" : `Edit ${form.company_name}`}</h2>
          {err && <div className="vb-pill err mb-3">{err}</div>}
          <form onSubmit={submit} className="grid gap-3">
            <input className="vb-input" required placeholder="Company name" value={form.company_name || ""} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="ten-company" />
            <label className="text-xs uppercase font-semibold opacity-70">Subscription tier</label>
            <select className="vb-input" required value={form.tier_id || ""} onChange={(e) => setForm({ ...form, tier_id: e.target.value })} data-testid="ten-tier">
              {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} — ${t.price_monthly}/mo · {t.features.max_sites} sites · {t.features.max_users} users</option>)}
            </select>

            {addOns.length > 0 && (
              <div className="border rounded-lg p-3" style={{ borderColor: "var(--vb-border)" }} data-testid="ten-addons">
                <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="ten-addons-toggle">
                  <input type="checkbox" checked={showTenantAddons}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setShowTenantAddons(on);
                      if (!on) setForm((f) => ({ ...f, enabled_addons: [] }));
                    }}
                    data-testid="ten-addons-checkbox" />
                  <span className="text-xs uppercase font-semibold opacity-70">Add-on features</span>
                  <span className="text-[11px] opacity-50">{showTenantAddons ? "(untick to clear)" : "(tick to show the full list)"}</span>
                </label>
                {showTenantAddons && (
                  <>
                    <p className="text-[11px] opacity-60 mt-2 mb-2">Optional features enabled only for customers that need them.</p>
                    <div className="grid gap-2">
                      {addOns.map((a) => {
                        const on = (form.enabled_addons || []).includes(a.key);
                        return (
                          <label key={a.key} className="flex items-start gap-2 text-sm cursor-pointer" data-testid={`ten-addon-${a.key}`}>
                            <input
                              type="checkbox" checked={on} onChange={() => toggleAddon(a.key)}
                              className="mt-1" data-testid={`ten-addon-checkbox-${a.key}`}
                            />
                            <span>
                              <span className="font-semibold" style={{ color: "var(--vb-text-2)" }}>{a.label}</span>
                              <span className="block text-[11px] opacity-60">{a.description}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {show === "create" ? (
              <>
                {/* Billing model selector */}
                <label className="text-xs uppercase font-semibold opacity-70">Billing model</label>
                <div className="grid grid-cols-2 gap-2" data-testid="ten-billing-model">
                  {[
                    ["invoiced", "Direct / Invoiced", "We issue an invoice; admin sets the service window."],
                    ["subscription", "Subscription (Stripe)", "Customer pays by card. Stripe checkout — Phase 2."],
                  ].map(([val, label, hint]) => (
                    <button
                      type="button" key={val}
                      onClick={() => setForm({ ...form, billing_model: val })}
                      data-testid={`ten-model-${val}`}
                      className="text-left p-3 rounded-lg border transition-colors"
                      style={{
                        borderColor: form.billing_model === val ? "var(--vb-text-2)" : "var(--vb-border)",
                        background: form.billing_model === val ? "rgba(120,61,25,0.06)" : "transparent",
                      }}
                    >
                      <div className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>{label}</div>
                      <div className="text-[11px] opacity-60 mt-0.5">{hint}</div>
                    </button>
                  ))}
                </div>

                {form.billing_model === "invoiced" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase font-semibold opacity-70">Service start date</label>
                      <input className="vb-input" type="date" value={form.service_start || ""} onChange={(e) => setForm({ ...form, service_start: e.target.value })} data-testid="ten-service-start" />
                    </div>
                    <div>
                      <label className="text-xs uppercase font-semibold opacity-70">Duration (months)</label>
                      <input className="vb-input" type="number" min={1} value={form.duration_months ?? 12} onChange={(e) => setForm({ ...form, duration_months: Number(e.target.value) })} data-testid="ten-duration" />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="text-xs uppercase font-semibold opacity-70">Billing interval</label>
                    <select className="vb-input" value={form.billing_interval || "monthly"} onChange={(e) => setForm({ ...form, billing_interval: e.target.value })} data-testid="ten-interval">
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                    <p className="text-[11px] opacity-60 mt-1">Stripe checkout + auto-renewal wiring ships in Phase 2. For now the trial window applies.</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase font-semibold opacity-70">Trial period (days)</label>
                    <input className="vb-input" type="number" min={0} value={form.trial_days ?? 0} onChange={(e) => setForm({ ...form, trial_days: Number(e.target.value) })} data-testid="ten-trial-days" />
                  </div>
                  <div>
                    <label className="text-xs uppercase font-semibold opacity-70">Grace period (days)</label>
                    <input className="vb-input" type="number" min={0} value={form.grace_days ?? 0} onChange={(e) => setForm({ ...form, grace_days: Number(e.target.value) })} data-testid="ten-grace-days" />
                  </div>
                </div>
                <p className="text-[11px] opacity-60 -mt-1">Trial applies at the start of service. Grace is a buffer after the service ends before the workspace is locked.</p>

                <div className="border-t pt-3 mt-1"><strong className="vb-heading" style={{ color: "var(--vb-text-2)" }}>Initial owner account</strong>
                  <p className="text-xs opacity-60 m-0">We only seed the Owner. The customer adds their own team afterwards.</p>
                </div>
                <input className="vb-input" required placeholder="Owner full name" value={form.owner_name || ""} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="ten-owner-name" />
                <input className="vb-input" required placeholder="Owner email / username" value={form.owner_email || ""} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="ten-owner-email" />
                <div className="flex gap-2 items-stretch">
                  <input className="vb-input flex-1" required placeholder="Owner password" value={form.owner_password || ""} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} data-testid="ten-owner-pwd" />
                  <button
                    type="button"
                    className="vb-btn ghost"
                    onClick={() => setForm((f) => ({ ...f, owner_password: generateTempPassword(12) }))}
                    title="Generate a strong random password"
                    data-testid="ten-owner-pwd-generate"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    <Sparkles size={13} /> Generate
                  </button>
                </div>
              </>
            ) : (
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            )}
            <div className="flex gap-2 justify-end"><button type="button" className="vb-btn ghost" onClick={() => setShow(null)}>Cancel</button>
              <button type="submit" className="vb-btn" disabled={busy} data-testid="ten-submit">{busy ? "Saving…" : "Save"}</button></div>
          </form>
        </div></div></Portal>
      )}

      {editingUser && (
        <EditTenantUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={async () => { setEditingUser(null); await load(); }}
        />
      )}

      {billingTenant && (
        <TenantBillingModal
          tenant={billingTenant}
          tiers={tiers}
          autoGenerate={billingAutoGen}
          onClose={() => { setBillingTenant(null); setBillingAutoGen(false); }}
          onChanged={load}
        />
      )}

      {packageTenant && (
        <TenantPackageModal
          tenant={packageTenant}
          tiers={tiers}
          onClose={() => setPackageTenant(null)}
          onChanged={load}
        />
      )}

      {qrTenant && (
        <QRStickerModal tenant={qrTenant} onClose={() => setQrTenant(null)} />
      )}

      {tempPwd && (
        <Portal><div className="vb-modal-bg">
          <div className="vb-modal">
            <h2>Temporary password for {tempPwd.user.name}</h2>
            <p className="text-sm opacity-80 mb-3">
              Share this securely (e.g. over a phone call). The user will be required to set their own password on next login.
            </p>
            <div className="vb-card p-4 flex items-center justify-between gap-3 mb-4 font-mono text-lg" data-testid="temp-pwd-display">
              {tempPwd.password}
              <button className="vb-btn ghost" onClick={copyTemp} data-testid="copy-temp">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="text-right">
              <button className="vb-btn" onClick={() => setTempPwd(null)} data-testid="close-temp">Close</button>
            </div>
          </div>
        </div></Portal>
      )}
    </div>
  );
}

const ROLE_RANK = { owner: 0, manager: 1, supervisor: 2, staff: 3 };

function fmtD(iso) { return iso ? new Date(iso).toLocaleDateString() : "—"; }
function fmtSGD(n) { return new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD" }).format(Number(n || 0)); }

// Billing lifecycle badge — mirrors compute_billing_state statuses.
function LifecycleBadge({ state }) {
  const s = state?.status || "unknown";
  if (s === "grace") {
    return <span className="vb-pill warn" data-testid="lifecycle-badge" title="In grace period — full access, renewal overdue">Grace · {state?.days_until_hard_block ?? 0}d</span>;
  }
  if (s === "expired") {
    return <span className="vb-pill err" data-testid="lifecycle-badge" title="Access window + grace lapsed — workspace is read-only">Expired · read-only</span>;
  }
  if (s === "suspended") {
    return <span className="vb-pill err" data-testid="lifecycle-badge">Suspended</span>;
  }
  if (s === "trialing") {
    return <span className="vb-pill" data-testid="lifecycle-badge" title={`Trial ends ${fmtD(state?.access_until)}`}>Trial · {state?.days_until_access_end ?? "—"}d</span>;
  }
  if (s === "demo") {
    return <span className="vb-pill" data-testid="lifecycle-badge">Demo</span>;
  }
  if (s === "active") {
    const soon = state?.days_until_access_end != null && state.days_until_access_end <= 30;
    return <span className={`vb-pill ${soon ? "warn" : "ok"}`} data-testid="lifecycle-badge" title={state?.access_until ? `Renews ${fmtD(state.access_until)}` : "Unrestricted"}>
      {soon ? `Active · ${state.days_until_access_end}d` : "Active"}
    </span>;
  }
  return <span className="vb-pill" data-testid="lifecycle-badge">—</span>;
}


function EditTenantUserModal({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {};
      if (form.name.trim() !== (user.name || "")) payload.name = form.name.trim();
      if (form.email.trim().toLowerCase() !== (user.email || "")) payload.email = form.email.trim();
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.put(`/admin/users/${user.id}`, payload);
      onSaved();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <Portal><div className="vb-modal-bg" onClick={onClose} data-testid="edit-tenant-user-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }} data-testid="edit-tenant-user-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">Edit customer user</h2>
            <p className="text-xs opacity-60 m-0">Fix typos in name / email. Role &amp; tenant are not changed here.</p>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="edit-tenant-user-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="edit-tenant-user-form">
          <div>
            <label className="text-xs opacity-70">Full name *</label>
            <input className="vb-input" required maxLength={120} value={form.name} onChange={set("name")} data-testid="edit-tu-name" />
          </div>
          <div>
            <label className="text-xs opacity-70">Email *</label>
            <input className="vb-input" required type="email" value={form.email} onChange={set("email")} data-testid="edit-tu-email" />
            {user.email !== form.email.trim().toLowerCase() && (
              <p className="text-[11px] opacity-60 mt-1">Changing email also changes their login identifier — let them know first.</p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="edit-tu-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="edit-tu-submit">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div></Portal>
  );
}


function TermsAcceptanceBanner({ tenant }) {
  const acc = tenant.terms_acceptance;
  const links = acc
    ? [
        ["Terms & Conditions", acc.terms_url],
        ["Privacy Policy", acc.privacy_url],
        ["AI Disclaimer", acc.ai_disclaimer_url],
      ].filter(([, url]) => !!url)
    : [];

  if (!acc) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-2.5 text-xs border-b"
        style={{ borderColor: "var(--vb-border)", color: "#92400e", background: "rgba(217, 119, 6, 0.08)" }}
        data-testid={`terms-status-${tenant.id}`}
      >
        <FileClock size={15} />
        <span>Terms &amp; Conditions <strong>not yet accepted</strong> — the owner will agree during onboarding.</span>
      </div>
    );
  }

  const when = acc.accepted_at ? new Date(acc.accepted_at).toLocaleString() : "—";
  return (
    <div
      className="px-4 py-3 border-b text-xs"
      style={{ borderColor: "var(--vb-border)", background: "rgba(22, 163, 74, 0.06)" }}
      data-testid={`terms-status-${tenant.id}`}
    >
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 font-semibold" style={{ color: "#15803d" }}>
          <FileCheck2 size={15} /> Terms accepted
        </span>
        <span className="opacity-80">
          by <strong>{acc.accepted_by_name || "—"}</strong>
          {acc.accepted_by_email ? ` (${acc.accepted_by_email})` : ""}
        </span>
        <span className="opacity-70">on {when}</span>
        {acc.policy_version && <span className="vb-pill text-[10px]">v{acc.policy_version}</span>}
      </div>
      {links.length > 0 && (
        <div className="flex flex-wrap gap-3 mt-1.5">
          {links.map(([label, url]) => (
            <a
              key={label}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 vb-link opacity-80 hover:opacity-100"
            >
              {label} <ExternalLink size={11} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
